/**
 * The persistent Notification Center (STEP 11) — distinct from the
 * ephemeral toast queue (`react-hot-toast`, fire-and-forget, never
 * stored). A toast can optionally also push a durable entry here
 * (`components/notifications/notify.ts` composes both) when it
 * represents something worth an unread badge, e.g. "Lab result ready"
 * — not every toast needs one (e.g. "Saved" doesn't).
 */
import { create } from 'zustand';

export type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';

export interface AppNotification {
  id: string;
  title: string;
  message?: string;
  severity: NotificationSeverity;
  createdAt: string;
  read: boolean;
  /** Optional in-app route to navigate to on click. */
  href?: string;
}

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  /** Returns the new entry's id, so the caller can mark it read once the user has acknowledged it. */
  add: (notification: Omit<AppNotification, 'id' | 'createdAt' | 'read'>) => string;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  clear: () => void;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
  notifications: [],
  unreadCount: 0,

  add: (notification) => {
    const id = crypto.randomUUID();
    set((state) => {
      const entry: AppNotification = {
        ...notification,
        id,
        createdAt: new Date().toISOString(),
        read: false,
      };
      return {
        notifications: [entry, ...state.notifications].slice(0, 200),
        unreadCount: state.unreadCount + 1,
      };
    });
    return id;
  },

  markRead: (id) =>
    set((state) => {
      const target = state.notifications.find((n) => n.id === id);
      if (!target || target.read) return state;
      return {
        notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }),

  markAllRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),

  remove: (id) =>
    set((state) => {
      const target = state.notifications.find((n) => n.id === id);
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: target && !target.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }),

  clear: () => set({ notifications: [], unreadCount: 0 }),
}));
